import { useEffect, useState } from 'react';
import { Eye, EyeOff, Wifi, Shield, Building2, Users, Mail } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import vpnUserService, { type DemoAccountType } from '../../services/vpnUser.service';
import appDeviceService, { type DeviceGroup } from '../../services/appDevice.service';
import tenantService, { type Tenant } from '../../services/tenant.service';
import roleService, { type Role } from '../../services/role.service';
import { authService, type PasswordPolicy } from '../../services/auth.service';
import PasswordPolicyHints, { validatePassword } from '../auth/PasswordPolicyHints';

interface AddDemoUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const randomSuffix = () => Math.random().toString(36).slice(2, 8);

/**
 * SuperAdmin-only: create a fully-activated demo account (VPN User or Tenant
 * Admin) with a known password. No invite/email flow — the account is ready to
 * use immediately. Useful for demos and testing.
 */
const AddDemoUserModal = ({ isOpen, onClose, onCreated }: AddDemoUserModalProps) => {
  const toast = useToast();

  const [accountType, setAccountType] = useState<DemoAccountType>('vpn_user');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [deviceGroupId, setDeviceGroupId] = useState('');
  const [maxDevices, setMaxDevices] = useState(0);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialise form on open
  useEffect(() => {
    if (!isOpen) return;
    setAccountType('vpn_user');
    setEmail(`demo-${randomSuffix()}@securelink.local`);
    setDisplayName('');
    setPassword('');
    setConfirm('');
    setShowPassword(false);
    setTenantId('');
    setRoleId('');
    setDeviceGroupId('');
    setMaxDevices(0);
    setLoadingData(true);
    Promise.all([
      tenantService.getSelect().catch(() => [] as Tenant[]),
      authService.getPasswordPolicy().catch(() => null),
    ])
      .then(([tenantList, pol]) => {
        setTenants(tenantList);
        setPolicy(pol);
      })
      .finally(() => setLoadingData(false));
  }, [isOpen]);

  // Load roles / device groups when tenant changes
  useEffect(() => {
    if (!isOpen || !tenantId) {
      setRoles([]);
      setDeviceGroups([]);
      setRoleId('');
      setDeviceGroupId('');
      return;
    }
    roleService.list({ tenantId })
      .then((res) => {
        const items = res.items || [];
        setRoles(items);
        const defaultRole = items.find((r) => r.role_type === 'Viewer') || items[0];
        if (defaultRole) setRoleId(defaultRole.id);
      })
      .catch(() => setRoles([]));
    appDeviceService.listDeviceGroups({ limit: 100 })
      .then((res) => setDeviceGroups(res.items || []))
      .catch(() => setDeviceGroups([]));
  }, [isOpen, tenantId]);

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error('Validation Error', 'Email is required');
      return;
    }
    if (!displayName.trim()) {
      toast.error('Validation Error', 'Display name is required');
      return;
    }
    if (!tenantId) {
      toast.error('Validation Error', 'Please select a tenant');
      return;
    }
    if (password !== confirm) {
      toast.error('Validation Error', 'Passwords do not match');
      return;
    }
    if (policy) {
      const policyError = validatePassword(policy, password, email);
      if (policyError) {
        toast.error('Validation Error', `Password requirement not met: ${policyError}`);
        return;
      }
    } else if (password.length < 8) {
      toast.error('Validation Error', 'Password must be at least 8 characters');
      return;
    }
    if (accountType === 'tenant_admin' && !roleId) {
      toast.error('Validation Error', 'Please select a role for the tenant admin');
      return;
    }
    if (accountType === 'vpn_user' && !deviceGroupId) {
      toast.error('Validation Error', 'Please select a device group');
      return;
    }

    setSaving(true);
    try {
      const result = await vpnUserService.createDemoUser({
        accountType,
        email: email.trim(),
        displayName: displayName.trim(),
        password,
        tenantId,
        ...(accountType === 'tenant_admin' ? { roleId } : {}),
        ...(accountType === 'vpn_user' ? { deviceGroupId, maxDevices } : {}),
      });
      toast.success(
        'Demo User Created',
        `${result.email} is ready to use — sign in with the password you entered (not stored anywhere else).`
      );
      onCreated();
      onClose();
    } catch (err: any) {
      toast.error(
        'Create Failed',
        err?.response?.data?.error?.message || err?.message || 'Failed to create demo user'
      );
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary" disabled={saving}>
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="btn-primary inline-flex items-center space-x-2"
        disabled={saving || loadingData}
      >
        <span>{saving ? 'Creating…' : 'Create Demo User'}</span>
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Demo User" size="lg" footer={footer}>
      <div className="space-y-5">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">SuperAdmin demo account</p>
            <p className="text-xs text-amber-700">
              Creates a fully-activated account with the password you set — no invite or email is
              sent. Intended for demos and testing.
            </p>
          </div>
        </div>

        {/* Account type toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setAccountType('vpn_user')}
              className={`flex-1 inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                accountType === 'vpn_user'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Wifi className="w-4 h-4" />
              <span>VPN User</span>
            </button>
            <button
              type="button"
              onClick={() => setAccountType('tenant_admin')}
              className={`flex-1 inline-flex items-center justify-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                accountType === 'tenant_admin'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span>Tenant Admin</span>
            </button>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center space-x-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span>Email <span className="text-red-500">*</span></span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-base w-full"
            autoComplete="off"
          />
        </div>

        {/* Display name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="input-base w-full"
            placeholder="Demo User"
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-base w-full pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <PasswordPolicyHints policy={policy} password={password} email={email} />
        </div>

        {/* Confirm password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Confirm Password <span className="text-red-500">*</span>
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-base w-full"
            autoComplete="new-password"
          />
        </div>

        {/* Tenant selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center space-x-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span>Tenant <span className="text-red-500">*</span></span>
          </label>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="input-base w-full"
            disabled={loadingData}
          >
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        {/* Tenant Admin → role selector */}
        {accountType === 'tenant_admin' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center space-x-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span>Role <span className="text-red-500">*</span></span>
            </label>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              className="input-base w-full"
              disabled={!tenantId}
            >
              <option value="">Select a role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name || r.roleName} {r.role_type ? `(${r.role_type})` : ''}
                </option>
              ))}
            </select>
            {!tenantId && (
              <p className="text-xs text-gray-500 mt-1">Select a tenant to load roles.</p>
            )}
          </div>
        )}

        {/* VPN User → device group + max devices */}
        {accountType === 'vpn_user' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center space-x-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span>Device Group <span className="text-red-500">*</span></span>
              </label>
              <select
                value={deviceGroupId}
                onChange={(e) => setDeviceGroupId(e.target.value)}
                className="input-base w-full"
                disabled={!tenantId}
              >
                <option value="">Select a device group…</option>
                {deviceGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
              {!tenantId && (
                <p className="text-xs text-gray-500 mt-1">Select a tenant to load device groups.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Devices</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min={0}
                  max={1000}
                  value={maxDevices}
                  onChange={(e) => setMaxDevices(parseInt(e.target.value, 10) || 0)}
                  className="input-base w-24"
                />
                <span className="text-xs text-gray-400">0 = Unlimited</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddDemoUserModal;
